import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaFilter, FaFileExcel } from 'react-icons/fa';
import CustomDatePicker from '../../../../../../components/common/CustomDatePicker/CustomDatePicker';
import './CompactFilterBar.scss';

type ReviewsSection = 'general' | 'usuarios' | 'listado';

interface CompactFilterBarProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onShowMobileFilters?: () => void;
  // Reviews specific props
  section?: ReviewsSection;
  soloSesionActual?: boolean;
  onSoloSesionActualChange?: (checked: boolean) => void;
  // Excel export
  onExportToExcel?: () => void;
}

type TimePeriod = '1S' | '1M' | '6M' | '1A';

// Mapeo de textos para los botones
const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  '1S': '1 Sem',
  '1M': '1 Mes', 
  '6M': '6 Mes',
  '1A': '1 Año'
};

const CompactFilterBar: React.FC<CompactFilterBarProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onShowMobileFilters,
  section = 'general',
  soloSesionActual = false,
  onSoloSesionActualChange,
  onExportToExcel
}) => {
  
  // Time period handlers
  const handleTimePeriodClick = (period: TimePeriod) => {
    const today = new Date();
    const endDateStr = today.toISOString().split('T')[0];
    let startDateStr = '';

    switch (period) {
      case '1S': // 1 Semana
        const oneWeekAgo = new Date(today);
        oneWeekAgo.setDate(today.getDate() - 7);
        startDateStr = oneWeekAgo.toISOString().split('T')[0];
        break;
      case '1M': // 1 Mes
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        startDateStr = oneMonthAgo.toISOString().split('T')[0];
        break;
      case '6M': // 6 Meses
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        startDateStr = sixMonthsAgo.toISOString().split('T')[0];
        break;
      case '1A': // 1 Año
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        startDateStr = oneYearAgo.toISOString().split('T')[0];
        break;
    }

    onStartDateChange(startDateStr);
    onEndDateChange(endDateStr);
  };

  // Determine active time period based on current dates
  const getActivePeriod = (): TimePeriod | null => {
    const today = new Date();
    const start = new Date(startDate);
    const diffTime = Math.abs(today.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 8) return '1S';
    if (diffDays <= 32) return '1M';
    if (diffDays <= 185) return '6M';
    if (diffDays <= 370) return '1A';
    
    return null;
  };

  const activePeriod = getActivePeriod();

  return (
    <div className='filter-no-reponsive'>
      <div className="transactions-filters-main-container">
        <div className="responsive-filters-container">
          <div className="filter-group-container">
            {/* Time Period Section */}
            <div className="time-period-section">
              <div className="time-buttons">
                {(['1S', '1M', '6M', '1A'] as TimePeriod[]).map((period) => (
                  <Button
                    key={period}
                    variant="none"
                    className={`time-button ${activePeriod === period ? 'active' : ''}`}
                    onClick={() => handleTimePeriodClick(period)}
                  >
                    {TIME_PERIOD_LABELS[period]}
                  </Button>
                ))}
              </div>
            </div>

            {/* Mobile Filter Button */}
            <Button
              onClick={onShowMobileFilters}
              variant="none"
              className="transactions-mobile-filter-button"
              aria-label="Abrir filtros"
            >
              <FaFilter />
            </Button>

            {/* Date Filter Section */}
            <div className="date-filter-section">
              <div className="date-filter-group">
                <CustomDatePicker
                  value={startDate}
                  onChange={onStartDateChange}
                  disabled={soloSesionActual}
                  placeholder="Selecciona fecha inicio"
                />
              </div>
              <div className="date-filter-group">
                <CustomDatePicker
                  value={endDate}
                  onChange={onEndDateChange}
                  disabled={soloSesionActual}
                  placeholder="Selecciona fecha fin"
                />
              </div>
            </div>

            {/* Current Session Toggle */}
            <div className="current-session-toggle">
              <div className="mediopunto-form-check-input">
                <input
                  type="checkbox"
                  id="current-session-toggle"
                  checked={soloSesionActual}
                  onChange={(e) => onSoloSesionActualChange?.(e.target.checked)}
                />
                <label htmlFor="current-session-toggle">Sesión actual</label>
              </div>
            </div>

            {/* Excel Export Button */}
            <div className="export-section">
              <Button
                variant="none"
                className="transactions-export-button"
                onClick={onExportToExcel}
                title="Exportar reseñas a Excel"
              >
                <FaFileExcel className="export-icon" />
                <span className="export-text">Excel</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactFilterBar;
